import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import { GetDocumentsQuery, GetDocumentsQueryVariables, GET_DOCUMENTS } from '@neo.tax/shared';

export type Documents = GetDocumentsQuery['getDocuments'];

type GetDocumentsType =
  | {
      documents: Documents;
      loading: false;
    }
  | {
      documents: null;
      loading: true;
    };

type GetDocumentsTypeWithSkip =
  | {
      documents: null;
      loading: false;
    }
  | GetDocumentsType;

interface UseGetDocumentsParams {
  skip?: boolean;
  fiscalYearId: string | undefined;
}

export function useGetDocuments(params: UseGetDocumentsParams): GetDocumentsTypeWithSkip {
  const { fiscalYearId, skip = false } = params;
  const shouldSkip = skip || !fiscalYearId;

  const { data, error, loading } = useQuery<GetDocumentsQuery, GetDocumentsQueryVariables>(
    GET_DOCUMENTS,
    shouldSkip
      ? { skip: true }
      : {
          variables: { fiscalYearId },
        },
  );

  if (shouldSkip) {
    return { documents: null, loading: false };
  }

  if (loading) {
    return { documents: null, loading: true };
  }

  if (error) {
    throw error;
  }

  if (!data) {
    throw createError('MissingDocuments', 'Failed to load documents');
  }

  return { documents: data.getDocuments, loading: false };
}
